<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h1>Órdenes de Plataformas</h1>
          <p class="text-muted mb-0">Consulta las órdenes recibidas de las plataformas por fecha y status.</p>
        </b-col>
        <b-col cols="auto">
          <base-button type="primary" size="sm" :disabled="loading" @click="getOrders">
            <b-icon-arrow-clockwise></b-icon-arrow-clockwise> Actualizar
          </base-button>
        </b-col>
      </b-row>

      <!-- Filtros -->
      <b-card class="mb-4">
        <b-row>
          <b-col cols="12" sm="6" md="3">
            <b-form-group label="Desde:">
              <b-form-input type="date" v-model="filters.start_date"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6" md="3">
            <b-form-group label="Hasta:">
              <b-form-input type="date" v-model="filters.end_date"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6" md="2">
            <b-form-group label="Status:">
              <b-form-select v-model="filters.status">
                <b-form-select-option :value="''">Todos</b-form-select-option>
                <b-form-select-option v-for="s in statusOptions" :key="s" :value="s">{{ s }}</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6" md="2">
            <b-form-group label="Plataforma:">
              <b-form-select v-model="filters.platform_id">
                <b-form-select-option :value="''">Todas</b-form-select-option>
                <b-form-select-option v-for="p in platforms" :key="p.id" :value="p.id">{{ p.name }}</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6" md="2">
            <b-form-group label="Sucursal:">
              <b-form-select v-model="filters.sucursal_id">
                <b-form-select-option :value="''">Todas</b-form-select-option>
                <b-form-select-option v-for="s in sucursals" :key="s.id" :value="s.id">{{ s.nombre }}</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <base-button type="success" size="sm" :disabled="loading" @click="getOrders">Buscar</base-button>
            <base-button type="secondary" size="sm" :disabled="loading" @click="resetFilters">Limpiar</base-button>
          </b-col>
        </b-row>
      </b-card>

      <!-- Resumen -->
      <b-row class="mb-3">
        <b-col cols="6" md="3">
          <b-card no-body class="bg-info">
            <b-card-body>
              <b-card-title title-tag="h5" class="text-uppercase mb-0 text-white">Total Órdenes</b-card-title>
              <span class="h2 font-weight-bold mb-0 text-white">{{ orders.length }}</span>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col cols="6" md="3">
          <b-card no-body class="bg-success">
            <b-card-body>
              <b-card-title title-tag="h5" class="text-uppercase mb-0 text-white">Venta Total</b-card-title>
              <span class="h2 font-weight-bold mb-0 text-white">{{ totalSales | money }}</span>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col cols="6" md="3">
          <b-card no-body class="bg-warning">
            <b-card-body>
              <b-card-title title-tag="h5" class="text-uppercase mb-0 text-white">Sin confirmar</b-card-title>
              <span class="h2 font-weight-bold mb-0 text-white">{{ unacknowledgedCount }}</span>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col cols="6" md="3">
          <b-card no-body class="bg-danger">
            <b-card-body>
              <b-card-title title-tag="h5" class="text-uppercase mb-0 text-white">Canceladas</b-card-title>
              <span class="h2 font-weight-bold mb-0 text-white">{{ cancelledCount }}</span>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Tabla -->
      <b-card>
        <b-table
          stacked="md"
          striped
          hover
          responsive
          :busy="loading"
          :items="orders"
          :fields="tableFields"
          :per-page="perPage"
          :current-page="page"
          show-empty
          empty-text="No hay órdenes para los filtros seleccionados."
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong class="ml-2">Cargando...</strong>
            </div>
          </template>
          <template #cell(created_at)="data">
            {{ formatDate(data.item.created_at) }}
          </template>
          <template #cell(platform_id)="data">
            {{ platformName(data.item.platform_id) }}
          </template>
          <template #cell(sucursal_id)="data">
            {{ sucursalName(data.item.sucursal_id) }}
          </template>
          <template #cell(total_order)="data">
            {{ data.item.total_order | money }}
          </template>
          <template #cell(status)="data">
            <b-badge :variant="statusVariant(data.item.status)">{{ data.item.status || 'N/A' }}</b-badge>
          </template>
          <template #cell(acknowledged_sucursal)="data">
            <b-icon-check-circle-fill v-if="data.item.acknowledged_sucursal" variant="success"></b-icon-check-circle-fill>
            <b-icon-x-circle-fill v-else variant="danger"></b-icon-x-circle-fill>
          </template>
          <template #cell(actions)="data">
            <b-button size="sm" variant="outline-info" @click="showDetails(data.item)">Detalles</b-button>
          </template>
        </b-table>
        <b-pagination
          v-if="orders.length > perPage"
          v-model="page"
          :total-rows="orders.length"
          :per-page="perPage"
          align="center"
        ></b-pagination>
      </b-card>

      <!-- Modal Detalles -->
      <b-modal id="platform-order-details" :title="'Orden #' + (selectedOrder.id || '')" size="lg" ok-only ok-title="Cerrar">
        <div v-if="selectedOrder.id">
          <b-row>
            <b-col md="6">
              <p><strong>Plataforma:</strong> {{ platformName(selectedOrder.platform_id) }}</p>
              <p><strong>ID en plataforma:</strong> {{ selectedOrder.platform_order_id }}</p>
              <p><strong>Sucursal:</strong> {{ sucursalName(selectedOrder.sucursal_id) }}</p>
              <p><strong>Status:</strong> <b-badge :variant="statusVariant(selectedOrder.status)">{{ selectedOrder.status }}</b-badge></p>
              <p><strong>Fecha:</strong> {{ formatDate(selectedOrder.created_at) }}</p>
            </b-col>
            <b-col md="6">
              <p><strong>Cliente:</strong> {{ selectedOrder.customer_name || 'N/A' }}</p>
              <p><strong>Teléfono:</strong> {{ selectedOrder.customer_phone || 'N/A' }}</p>
              <p><strong>Método de pago:</strong> {{ selectedOrder.payment_method || 'N/A' }}</p>
              <p><strong>Método de entrega:</strong> {{ selectedOrder.delivery_method || 'N/A' }}</p>
              <p><strong>Total:</strong> {{ selectedOrder.total_order | money }}</p>
            </b-col>
          </b-row>

          <h6 class="mt-3">Historial de Status</h6>
          <b-table
            v-if="selectedOrder.status_history && selectedOrder.status_history.length"
            small
            striped
            :items="selectedOrder.status_history"
            :fields="[
              { key: 'status', label: 'Status' },
              { key: 'comments', label: 'Comentarios' },
              { key: 'created_at', label: 'Fecha' },
            ]"
          >
            <template #cell(status)="data">
              <b-badge :variant="statusVariant(data.item.status)">{{ data.item.status }}</b-badge>
            </template>
            <template #cell(created_at)="data">
              {{ formatDate(data.item.created_at) }}
            </template>
          </b-table>
          <p v-else class="text-muted">Sin historial disponible.</p>
        </div>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import Nav from "../components/Nav.vue";
import axios from "axios";

export default {
  name: "PlatformOrders",
  components: { Nav },
  filters: {
    money(value) {
      const n = Number(value || 0);
      return "$" + n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      loading: false,
      orders: [],
      platforms: [],
      sucursals: [],
      page: 1,
      perPage: 25,
      selectedOrder: {},
      filters: {
        start_date: today,
        end_date: today,
        status: "",
        platform_id: "",
        sucursal_id: "",
      },
      tableFields: [
        { key: "id", label: "ID", sortable: true },
        { key: "created_at", label: "Fecha", sortable: true },
        { key: "platform_id", label: "Plataforma", sortable: true },
        { key: "sucursal_id", label: "Sucursal", sortable: true },
        { key: "platform_order_id", label: "Orden Plataforma" },
        { key: "customer_name", label: "Cliente" },
        { key: "total_order", label: "Total", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "acknowledged_sucursal", label: "Confirmada" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    totalSales() {
      return this.orders.reduce((sum, o) => sum + Number(o.total_order || 0), 0);
    },
    unacknowledgedCount() {
      return this.orders.filter((o) => !o.acknowledged_sucursal).length;
    },
    cancelledCount() {
      return this.orders.filter((o) => (o.status || "").toUpperCase().includes("CANCEL")).length;
    },
    statusOptions() {
      const set = new Set();
      this.orders.forEach((o) => {
        if (o.status) set.add(o.status);
      });
      return Array.from(set).sort();
    },
  },
  created() {
    this.getPlatforms();
    this.getSucursals();
    this.getOrders();
  },
  activated() {
    this.getOrders();
  },
  methods: {
    getOrders() {
      let that = this;
      this.loading = true;
      const params = {};
      if (this.filters.start_date) params.start_date = this.filters.start_date;
      if (this.filters.end_date) params.end_date = this.filters.end_date;
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.platform_id) params.platform_id = this.filters.platform_id;
      if (this.filters.sucursal_id) params.sucursal_id = this.filters.sucursal_id;
      params.include_history = true;
      axios
        .get("/platform/orders", { params })
        .then(function (response) {
          that.orders = Array.isArray(response.data) ? response.data : [];
          that.page = 1;
        })
        .catch(function (err) {
          that.makeToast("Error", err.request ? err.request.response : err.message, "danger");
        })
        .finally(function () {
          that.loading = false;
        });
    },
    getPlatforms() {
      let that = this;
      axios
        .get("/platforms")
        .then(function (response) {
          that.platforms = Array.isArray(response.data) ? response.data : [];
        })
        .catch(function () {
          that.platforms = [];
        });
    },
    getSucursals() {
      let that = this;
      axios
        .get("/sucursals")
        .then(function (response) {
          that.sucursals = Array.isArray(response.data) ? response.data : [];
        })
        .catch(function () {
          that.sucursals = [];
        });
    },
    resetFilters() {
      const today = new Date().toISOString().slice(0, 10);
      this.filters = {
        start_date: today,
        end_date: today,
        status: "",
        platform_id: "",
        sucursal_id: "",
      };
      this.getOrders();
    },
    showDetails(order) {
      this.selectedOrder = order;
      this.$bvModal.show("platform-order-details");
    },
    platformName(id) {
      const p = this.platforms.find((x) => x.id === id);
      return p ? p.name : id;
    },
    sucursalName(id) {
      const s = this.sucursals.find((x) => x.id === id);
      return s ? s.nombre : id;
    },
    statusVariant(status) {
      const s = (status || "").toUpperCase();
      if (s.includes("CANCEL") || s.includes("REJECT") || s.includes("FAIL")) return "danger";
      if (s.includes("COMPLET") || s.includes("DELIVER") || s.includes("READY")) return "success";
      if (s.includes("ACCEPT") || s.includes("PROGRESS") || s.includes("PREPAR")) return "primary";
      if (s.includes("PENDING") || s.includes("CREATED")) return "warning";
      return "secondary";
    },
    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleString("es-MX");
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    },
  },
};
</script>
